<template>
  <div class="card mb-2">
    <div class="card-body bg-secondary pt-2 pb-0 pl-1 pr-2">
      <b-form class="search-form" @submit.prevent="onSearch">
        <div class="d-flex">
          <div class="row align-items-center">
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('receipt_no')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="receipt_no"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('item_no')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="item_no"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('sale_no')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="sale"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('lot_no')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="lot_no"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('title')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="title"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('status')">
              <input-widget :model="searchModel"
                            attribute="status"
                            value-field="value"
                            display-field="${item.key}"
                            type="select2"
                            :label="false"
                            :options="{placeholder: $t('Status')}"
                            :items="statuses"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('recommended_by_user_uuid')">
              <user-select-input-widget @select="onRecommendedByUserSelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Recommended By User')}"
                                        attribute="recommended_by_user_uuid" is-search/>
            </div>
            <div class="input-wrapper"
                 v-if="!excludeSearchFields.includes('negotiation_status')">
              <input-widget :model="searchModel"
                            attribute="negotiation_status"
                            value-field="value"
                            display-field="${item.key}"
                            type="select2"
                            :label="false"
                            :options="{placeholder: $t('Negotiation Status')}"
                            :items="negotiationStatuses"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('start_date')">
              <b-form-group>
                <date-range-picker v-model="startDateModel" class="picker" :ranges="false" singleDatePicker
                                   showDropdowns appendToBody autoApply @update="onDatePickerApply">
                  <template v-slot:input="picker" style="min-width: 350px;">
                    <span v-if="!picker.startDate" class="text-muted">
                      {{ $t('Start Date') }}
                    </span>
                    <span v-else>
                      {{ picker.startDate | toDate }}
                    </span>
                  </template>
                  <template v-slot:footer="data">
                    <b-button variant="link" class="float-right text-decoration-none mr-2"
                              @click="clearStartDate(data)">
                      {{ $t('Clear') }}
                    </b-button>
                  </template>
                </date-range-picker>
              </b-form-group>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('end_date')">
              <b-form-group>
                <date-range-picker v-model="endDateModel" class="picker" :ranges="false" singleDatePicker showDropdowns
                                   appendToBody autoApply @update="onDatePickerApply">
                  <template v-slot:input="picker" style="min-width: 350px;">
                    <span v-if="!picker.endDate" class="text-muted">
                      {{ $t('End Date') }}
                    </span>
                    <span v-else>
                      {{ picker.endDate | toDate }}
                    </span>
                  </template>
                  <template v-slot:footer="data">
                    <b-button variant="link" class="float-right text-decoration-none mr-2" @click="clearEndDate(data)">
                      {{ $t('Clear') }}
                    </b-button>
                  </template>
                </date-range-picker>
              </b-form-group>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('customer_uuid')">
              <customer-select-input-widget :model="searchModel" :label="false" is-search
                                            attribute="customer_uuid" :options="{placeholder: $t('Vendor')}"
                                            @select="onCustomerSelect">
              </customer-select-input-widget>
            </div>
            <div class="input-wrapper" v-if="!enableContinuousAuction && !excludeSearchFields.includes('bidder_uuid')">
              <bidder-select-input-widget :model="searchModel" attribute="bidder_uuid" @select="onBidderSelect"
                                          :auction-uuid="extraFilters.auction_uuid" is-search
                                          :lot-uuid="extraFilters.lot_uuid"
                                          value-field="uuid"
                                          :label="false" :options="{placeholder: $t('Winning Bidder')}"/>
            </div>

            <div class="input-wrapper" v-if="!excludeSearchFields.includes('department_uuid')">
              <department-select-input-widget @select="onDepartmentSelect"
                                              @unselect="onDepartmentUnselect"
                                              :label="false"
                                              :options="{placeholder: $t('Department')}"
                                              :model="searchModel"
                                              attribute="department_uuid"/>
            </div>

            <div class="input-wrapper" v-if="!excludeSearchFields.includes('category_uuid')">
              <category-select-input-widget @select="onCategorySelect"
                                            :label="false" with-all-categories :department="searchModel.department"
                                            :options="{placeholder: $t('Category')}"
                                            :model="searchModel"
                                            attribute="category_uuid"/>
            </div>
            <div class="input-wrapper">
              <user-select-input-widget @select="onSpecialistSelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Specialist')}"
                                        attribute="specialist_uuid" is-search/>
            </div>
            <div class="input-wrapper">
              <cataloguing-lifecycle-select-input-widget attribute="cataloguing_lifecycle" :model="searchModel"
                            :label="false" :options="{placeholder: $t('Cataloguing Lifecycle')}"
                            :multiple="true"/>
            </div>
            <div class="input-wrapper">
              <photography-lifecycle-select-input-widget attribute="photography_lifecycle" :model="searchModel"
                            :label="false" :options="{placeholder: $t('Photography Lifecycle')}"
                            :multiple="true"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('account_manager_uuid')">
              <user-select-input-widget :model="searchModel" :label="false"
                                        is-search attribute="account_manager_uuid"
                                        :options="{placeholder: $t('Account Manager')}"
                                        @select="onUserSelect">
              </user-select-input-widget>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('invoice_status')">
              <invoice-status-select-input-widget :model="searchModel" attribute="invoice_status" :label="false"
                                                  :options="{placeholder: $t('Invoice Status')}" with-empty-status
                                                  :remove-statuses="['cancelled', 'credited']" multiple/>
            </div>
            <div class="input-wrapper">
              <reserve-type-select-input-widget :model="searchModel" attribute="reserve_type"
                                                :options="{placeholder: $t('Reserve Type')}" :label="false"/>
            </div>
            <div class="input-wrapper"
                 v-if="!excludeSearchFields.includes('auction_date_uuid') && enableTimedSessions && sale && sale.type === 'timed'">
              <input-widget :model="searchModel" attribute="auction_date_uuid"
                            display-field="${item.title}" type="select2" :options="{placeholder: $t('Session')}"
                            :items="sale.dates" :label="false"/>
            </div>
            <div class="input-wrapper"
                 v-if="!excludeSearchFields.includes('auction_collection_uuid')">
              <auction-collection-select-input-widget :model="searchModel" attribute="auction_collection_uuid"
                                                      :options="{placeholder: $t('Collection')}"
                                                      :items="sale? sale.auction_collections : []" :label="false"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('item_location_uuid')">
              <item-location-select-input-widget :model="searchModel" attribute="item_location_uuid" :label="false"
                                                 :placeholder="$t('Item Location')"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('source_of_sale')">
              <input-widget :model="searchModel" attribute="source_of_sale" :label="false" type="select2"
                            :items="saleSources" value-field="key" :options="{placeholder: $t('Auction Provider')}"
                            display-field="${item.value}" multiple/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="receipt_contract_status" type="select2"
                            :options="{placeholder: $t('Consignment Status')}"
                            :items="contractStatuses" value-field="key" :label="false" display-field="${item.text}">
              </input-widget>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('proposal_status')">
              <input-widget :model="searchModel" attribute="proposal_status" type="select2"
                            value-field="value" display-field="${item.key}" :label="false"
                            :options="{placeholder: $t('Proposal Status')}" :items="proposalStatusOptions"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('proposal_suggestion')">
              <input-widget :model="searchModel" attribute="proposal_suggestion" type="select2"
                            value-field="value" display-field="${item.key}" :label="false"
                            :options="{placeholder: $t('Proposal Suggestion')}" :items="suggestionOptions"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('images_value')">
              <input-widget :model="searchModel" attribute="images_value" type="select2"
                            value-field="value" display-field="${item.key}" :label="false"
                            :options="{placeholder: $t('Images')}" :items="imagesValues"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('hide_lot')">
              <input-widget :model="searchModel" attribute="hide_lot" type="select2"
                            value-field="value" display-field="${item.key}" :label="false"
                            :options="{placeholder: $t('Hide Lot')}" :items="hideLotValues"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('tax_status_uuid')">
              <tax-status-select-input-widget :model="searchModel" attribute="tax_status_uuid" :label="false"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('rep_user_uuid')">
              <user-select-input-widget attribute="rep_user_uuid" @select="onRepUserSelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Representative User')}"
                                        is-search/>
            </div>
            <template v-if="dynamicFieldsFilters.length > 0">
              <div class="input-wrapper" v-for="field in dynamicFieldsFilters" :key="field.key">
                <input-widget :type="field.type" :label="getDynamicFieldFilterLabel(field)" :placeholder="field.label"
                              :model="searchModel" :attribute="field.key"/>
              </div>
            </template>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('collected')">
              <input-widget :model="searchModel" attribute="collected" type="checkbox"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('unsettled')">
              <input-widget :model="searchModel" attribute="unsettled" type="checkbox"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('sold_via_negotiation')">
              <input-widget :model="searchModel" attribute="sold_via_negotiation" type="checkbox"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('use_images_for_auction_gallery')">
              <input-widget :model="searchModel" attribute="use_images_for_auction_gallery" type="checkbox"/>
            </div>
          </div>
          <div class="search-btn-wrapper">
            <b-button variant="primary" @click.prevent="onResetClick">
              <font-awesome-icon icon="undo" class="icon"/>
              {{ $t('Reset') }}
            </b-button>
            <b-button type="submit" variant="primary" class="ml-2">
              <font-awesome-icon icon="search" class="icon"/>
              {{ $t('Search') }}
            </b-button>
          </div>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import i18n from "../../shared/i18n";
  import DateRangePicker from 'vue2-daterange-picker'
  import {createNamespacedHelpers} from "vuex";
  import UserSelectInputWidget from "../../core/widgets/UserSelectInputWidget";
  import CustomerSelectInputWidget from "../../core/widgets/CustomerSelectInputWidget";
  import InvoiceStatusSelectInputWidget from "@/core/widgets/InvoiceStatusSelectInputWidget";
  import DepartmentSelectInputWidget from "@/core/widgets/DepartmentSelectInputWidget";
  import CategorySelectInputWidget from "@/core/widgets/CategorySelectInputWidget";
  import ReserveTypeSelectInputWidget from "@/core/widgets/ReserveTypeSelectInputWidget";
  import AuctionCollectionSelectInputWidget from "@/core/widgets/AuctionCollectionSelectInputWidget";
  import BidderSelectInputWidget from "@/core/widgets/BidderSelectInputWidget";
  import ItemLocationSelectInputWidget from "@/core/widgets/ItemLocationSelectInputWidget";
  import TaxStatusSelectInputWidget from "@/core/widgets/TaxStatusSelectInputWidget";
  import CataloguingLifecycleSelectInputWidget from "@/core/widgets/CataloguingLifecycleSelectInputWidget.vue";
  import PhotographyLifecycleSelectInputWidget from "@/core/widgets/PhotographyLifecycleSelectInputWidget.vue";

  const {mapActions, mapState} = createNamespacedHelpers('lots');
  const {mapState: unsoldProposalsMapState} = createNamespacedHelpers('vendors/unsoldProposals');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');

  export default {
    name: "LotSearch",
    components: {
      PhotographyLifecycleSelectInputWidget,
      CataloguingLifecycleSelectInputWidget,
      TaxStatusSelectInputWidget,
      ItemLocationSelectInputWidget,
      BidderSelectInputWidget,
      AuctionCollectionSelectInputWidget,
      ReserveTypeSelectInputWidget,
      CategorySelectInputWidget,
      DepartmentSelectInputWidget,
      InvoiceStatusSelectInputWidget,
      CustomerSelectInputWidget,
      UserSelectInputWidget,
      InputWidget,
      DateRangePicker
    },
    props: {
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      sale: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        startDateModel: {startDate: null},
        endDateModel: {endDate: null},
        contractStatuses: [
          {key: 'draft', text: i18n.t('Draft')},
          {key: 'signed', text: i18n.t('Signed')},
          {key: 'not_generated', text: i18n.t('N/A')},
        ],
        imagesValues: [
          {value: 'has_images', key: i18n.t('Has Images')},
          {value: 'without_images', key: i18n.t('Without Images')},
        ],
        hideLotValues: [
          {value: 'true', key: i18n.t('Is Hidden')},
          {value: 'false', key: i18n.t('Is not Hidden')},
        ]
      }
    },
    computed: {
      ...mapStateGlobal({
        saleSources: state => state.saleSources,
        enableContinuousAuction: state => state.enableContinuousAuction,
        enableTimedSessions: state => state.enableTimedSessions,
        dynamicFieldsFilters: (state) => {
          let result = [];
          if (state.dynamicFieldsFilters && state.dynamicFieldsFilters.lots) {
            result = state.dynamicFieldsFilters.lots;
          }

          return result;
        },
        negotiationStatuses: state => {
          return [
            ...[{key: i18n.t('N/A'), value: 'N/A'}],
            ...state.negotiationStatuses
          ]
        },
        itemLifecycleOptions: state => state.itemLifecycleOptions,
      }),
      ...unsoldProposalsMapState({
        suggestionOptions: state => state.suggestionOptions,
        proposalStatusOptions: state => state.proposalStatusOptions,
      }),
      ...mapState({
        searchModel: state => state.tableData[state.tableSource].searchModel,
        extraFilters: state => state.tableData[state.tableSource].extraFilters,
      }),
      statuses() {
        const arr = [
          {key: i18n.t('Lotted'), value: 'lotted'},
          {key: i18n.t('Sold'), value: 'sold'},
          {key: i18n.t('Withdrawn'), value: 'withdrawn'},
          {key: i18n.t('Reserved'), value: 'reserved'},
          {key: i18n.t('Unsold'), value: 'unsold'},
        ]
        if (this.enableContinuousAuction) {
          arr.push({key: i18n.t('Published'), value: 'published'})
        }
        return arr
      }
    },
    methods: {
      ...mapActions(['getTableData', 'updateSearchModel', 'resetTable', 'resetTableDataPaginationPage']),
      async onSearch() {
        await this.updateSearchModel(this.searchModel);
        this.resetTableDataPaginationPage();
        this.getTableData();
      },
      onResetClick() {
        this.startDateModel = {startDate: null};
        this.endDateModel = {endDate: null};
        this.resetTable();
      },
      onDatePickerApply() {
        if (this.startDateModel.startDate) {
          this.searchModel.start_date = this.startDateModel.startDate
        }
        if (this.endDateModel.endDate) {
          this.searchModel.end_date = this.endDateModel.endDate;
        }
      },
      async onCustomerSelect(customer) {
        this.updateSearchModel({customer});
      },
      async onUserSelect(user) {
        this.updateSearchModel({user});
      },
      clearStartDate(data) {
        data.clickCancel();
        this.startDateModel = {startDate: null};
        this.searchModel.start_date = null;
      },
      clearEndDate(data) {
        data.clickCancel();
        this.endDateModel = {endDate: null};
        this.searchModel.end_date = null;
      },
      async onRecommendedByUserSelect(recommended_by_user) {
        this.updateSearchModel({recommended_by_user});
      },
      async onDepartmentSelect(department) {
        this.updateSearchModel({department});
      },
      async onDepartmentUnselect() {
        this.updateSearchModel({department: null, department_uuid: null});
      },
      async onCategorySelect(category) {
        this.updateSearchModel({category});
      },
      getDynamicFieldFilterLabel(field) {
        if (field.type === 'checkbox') {
          return field.label;
        }
        return false;
      },
      async onSpecialistSelect(specialist) {
        this.updateSearchModel({specialist});
      },
      async onBidderSelect(bidder) {
        this.updateSearchModel({bidder, paddle_number: bidder.paddle_number});
      },
      async onRepUserSelect(rep_user) {
        this.updateSearchModel({rep_user});
      },
    },
  }
</script>

<style lang="scss" scoped>
  .picker {
    width: 100%;
  }
</style>
